import React, { useCallback, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import RefreshIcon from '@mui/icons-material/Refresh'
import API from '../../component/Axios'
import ListEmptyState from '../../component/ListEmptyState'
import ListTableContainer from '../../component/ListTableContainer'
import Pagination from '../../component/Pagination'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import Title from '../../component/Title'
import { formatLocalDateTime } from '../../utils/dateDisplay'
import { formatNumber } from '../../utils/numberDisplay'
import MembershipReconciliationStatusPanel from './MembershipReconciliationStatusPanel'
import {
    getReconciliationSummary,
    getSummaryNumber,
    RECONCILIATION_DRY_RUN_ACTION,
    resolveReconciliationStatus,
} from './membershipReconciliationDisplay'

const offset = 20

const SummaryText = ({ summary, field }) => (
    <Typography variant="body2" component="span">
        {formatNumber(getSummaryNumber(summary, field))}
    </Typography>
)

export default function MembershipReconciliation() {
    const history = useHistory()
    const [loading, setLoading] = useState(true)
    const [curPage, setCurPage] = useState(1)
    const [totalCount, setTotalCount] = useState(0)
    const [rows, setRows] = useState([])
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('error')
    const [messageSnackbar, setMessageSnackbar] = useState('')

    const getLogs = useCallback(async () => {
        setLoading(true)

        try {
            const { data } = await API.post('/api/v1/admin/membership/getOperationLogs', {
                page: curPage,
                offset,
                action: RECONCILIATION_DRY_RUN_ACTION,
            })

            if (data.code === 200) {
                setTotalCount(Number(data.data?.count || 0))
                setRows(data.data?.rows || [])
            } else if (data.code === 401 || data.code === 403) {
                history.push('/login')
            } else {
                setTotalCount(0)
                setRows([])
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message || '멤버십 점검 로그를 불러오지 못했습니다.')
            }
        } catch (_error) {
            setTotalCount(0)
            setRows([])
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('멤버십 점검 로그를 불러오지 못했습니다.')
        } finally {
            setLoading(false)
        }
    }, [curPage, history])

    useEffect(() => {
        getLogs()
    }, [getLogs])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    return (
        <Grid item xs={12}>
            <Box sx={{ mb: 2 }}>
                <MembershipReconciliationStatusPanel showLink={false} />
            </Box>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                    <Title>멤버십 정합성 점검 로그</Title>
                    <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={getLogs} disabled={loading}>
                        새로고침
                    </Button>
                </Box>
                {loading ? (
                    <Spiner />
                ) : (
                    <>
                        <ListTableContainer>
                            <Table size="small" sx={{ minWidth: 980 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>점검 시간</TableCell>
                                        <TableCell>상태</TableCell>
                                        <TableCell align="right">이슈</TableCell>
                                        <TableCell align="right">P0</TableCell>
                                        <TableCell align="right">P1</TableCell>
                                        <TableCell align="right">P2</TableCell>
                                        <TableCell align="right">승인필요</TableCell>
                                        <TableCell align="right">자동조치 후보</TableCell>
                                        <TableCell>관리자</TableCell>
                                        <TableCell>사유</TableCell>
                                        <TableCell>감사 로그</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={11}>
                                                <ListEmptyState
                                                    title="멤버십 점검 로그가 없습니다."
                                                    description="daily dry-run 배치가 실행되면 이 목록에 표시됩니다."
                                                />
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {rows.map((row) => {
                                        const summary = getReconciliationSummary(row)
                                        const status = resolveReconciliationStatus(row, new Date(row.regTime))
                                        const issueCount = getSummaryNumber(summary, 'issueCount')

                                        return (
                                            <TableRow
                                                key={row.logId}
                                                hover
                                                sx={issueCount > 0 ? { bgcolor: 'rgba(211, 47, 47, 0.04)' } : undefined}
                                            >
                                                <TableCell>{formatLocalDateTime(row.regTime)}</TableCell>
                                                <TableCell>
                                                    <Chip size="small" color={status.color} label={status.label} />
                                                </TableCell>
                                                <TableCell align="right"><SummaryText summary={summary} field="issueCount" /></TableCell>
                                                <TableCell align="right"><SummaryText summary={summary} field="p0Count" /></TableCell>
                                                <TableCell align="right"><SummaryText summary={summary} field="p1Count" /></TableCell>
                                                <TableCell align="right"><SummaryText summary={summary} field="p2Count" /></TableCell>
                                                <TableCell align="right"><SummaryText summary={summary} field="requiresApprovalCount" /></TableCell>
                                                <TableCell align="right"><SummaryText summary={summary} field="autoFixableCount" /></TableCell>
                                                <TableCell>{row.adminId || '-'}</TableCell>
                                                <TableCell>{row.reason || '-'}</TableCell>
                                                <TableCell>#{row.logId}</TableCell>
                                            </TableRow>
                                        )
                                    })}
                                </TableBody>
                            </Table>
                        </ListTableContainer>
                        <Pagination curPage={curPage} setCurPage={setCurPage} totalCount={totalCount} offset={offset} />
                    </>
                )}
            </Paper>
            <Snackbars open={openSnackbar} onClose={handleClose} severity={severitySnackbar} message={messageSnackbar} />
        </Grid>
    )
}
